import React, { useState, useEffect, useRef } from "react";
import PropTypes from "prop-types";
import { css } from "emotion";
import moment from "moment";
import { Droppable, Draggable } from "react-beautiful-dnd";
import { Link, useHistory } from "react-router-dom";
import { useFilters } from "app/deliveryTool/context/FiltersProvider";
import DeliveryRecoveryContainer from "app/deliveryTool/containers/DeliveryRecoveryContainer";
import CreateRoadmapInput from "app/deliveryTool/pages/itineraire/CreateRoadmapInput";

const OptimizedRouteDetail = (props) => {
  const {
    roadmap,
    dragDisabled,
    recoveryReallocation,
    handleChangeDeliveryMan,
    onRoadmapSaved,
  } = props;

  const history = useHistory();

  const filters = useFilters();
  const [localLoading, setLocalLoading] = useState(false);
  const [showMap, setShowMap] = useState(false);
  const mapRef = useRef(null);

  const styles = {
    title: {
      color: "#484b4c",
      fontSize: "1.5rem",
      margin: "1.5rem 0 0rem 0",
      fontWeight: "normal",
    },
    subtitle: {
      fontSize: "1.2rem",
      margin: "0rem 0 0rem 0",
      fontWeight: "normal",
    },
    back: {
      color: "#429853",
      fontSize: "1.0rem",
      cursor: "pointer",
    },
    center: {
      textAlign: "center",
    },
    block: {
      backgroundColor: "white",
      margin: "1.0rem 0.5rem",
      padding: "0.5rem",
    },
    statut: {
      textTransform: "uppercase",
      fontSize: "0.6rem",
      marginBottom: "0rem",
    },
    para: {
      marginBottom: "0rem",
      fontSize: "0.8rem",
    },
    input: {},
    mapPopup: {
      position: 'fixed',
      top: 0,
      left: 0,
      width: '100%',
      height: '100%',
      backgroundColor: 'rgba(0, 0, 0, 0.8)',
      zIndex: 1000,
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
    },
    mapContainer: {
      backgroundColor: 'white',
      borderRadius: '8px',
      padding: '20px',
      maxWidth: '90%',
      maxHeight: '90%',
      width: '800px',
      height: '600px',
      position: 'relative',
    },
    mapCloseButton: {
      position: 'absolute',
      top: '10px',
      right: '10px',
      background: '#ff4444',
      color: 'white',
      border: 'none',
      borderRadius: '50%',
      width: '30px',
      height: '30px',
      cursor: 'pointer',
      fontSize: '16px',
      zIndex: 1001,
    },
    mapContent: {
      width: '100%',
      height: '100%',
      borderRadius: '4px',
      overflow: 'hidden',
    },
    buttonGroup: {
      display: 'flex',
      gap: '10px',
      justifyContent: 'center',
      marginTop: '10px',
    },
  };

  const getListStyle = (isDraggingOver) => ({
    background: isDraggingOver ? "#429853" : "",
    border: isDraggingOver ? "1px solid #429853" : "1px solid #dedede",
    padding: "0px 5px",
    width: "100%",
    minHeight: "150px",
  });

  const updateDeliveryMan = async (event) => {
    const deliveryManId = event.target.value;
    if (deliveryManId !== "all") {
      try {
        setLocalLoading(true);
        // Mettre à jour le livreur dans l'état local
        handleChangeDeliveryMan(roadmap.id, deliveryManId);
        setLocalLoading(false);
      } catch (error) {
        console.error('Erreur lors de la mise à jour du livreur:', error);
        setLocalLoading(false);
      }
    }
  };

  const saveRoadmap = async () => {
    try {
      if (!roadmap || !roadmap.deliveries || roadmap.deliveries.length === 0) {
        console.error('Aucune livraison à sauvegarder');
        return;
      }

      setLocalLoading(true);
      
      // Appeler la fonction pour sauvegarder l'itinéraire
      if (onRoadmapSaved) {
        await onRoadmapSaved(roadmap.id);
        
        // Redirection vers la liste des itinéraires
        history.push({
          pathname: "/itineraires",
          search: window.location.search,
        });
      }
    } catch (error) {
      console.error('Erreur lors de la sauvegarde de la roadmap:', error);
    } finally {
      setLocalLoading(false);
    }
  };

  const showMapPopup = () => {
    setShowMap(true);
  };

  const closeMapPopup = () => {
    setShowMap(false);
  };

  // Composant de carte
  const RouteMap = () => {
    useEffect(() => {
      if (!showMap || !mapRef.current) return;

      // Charger Leaflet dynamiquement
      const loadLeaflet = async () => {
        try {
          // Charger les CSS de Leaflet
          const link = document.createElement('link');
          link.rel = 'stylesheet';
          link.href = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.css';
          document.head.appendChild(link);

          // Charger Leaflet
          const L = await import('leaflet');
          
          // Supprimer les icônes par défaut pour éviter les erreurs
          delete L.Icon.Default.prototype._getIconUrl;
          L.Icon.Default.mergeOptions({
            iconRetinaUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png',
            iconUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png',
            shadowUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png',
          });

          // Créer la carte
          const map = L.map(mapRef.current).setView([45.7613911, 4.9167728], 10);

          // Ajouter la couche OpenStreetMap
          L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
            attribution: '© OpenStreetMap contributors'
          }).addTo(map);

          // Coordonnées du Moulin (départ/arrivée)
          const MOULIN_COORDS = [45.7613911, 4.9167728];

          // Ajouter le marqueur du Moulin
          L.marker(MOULIN_COORDS)
            .addTo(map)
            .bindPopup('<b>Le Moulin</b><br>Départ/Arrivée')
            .openPopup();

          // Créer un tableau de coordonnées pour l'itinéraire
          const routeCoordinates = [MOULIN_COORDS];

          // Ajouter les coordonnées des livraisons dans l'ordre
          roadmap.deliveries.forEach((delivery, index) => {
            const coords = [delivery.delivery.lat, delivery.delivery.lng];
            routeCoordinates.push(coords);

            // Ajouter un marqueur pour chaque livraison
            const marker = L.marker(coords).addTo(map);
            const popupContent = `
              <b>${delivery.recovery ? 'Récupération' : 'Livraison'} #${index + 1}</b><br>
              ${delivery.delivery.company}<br>
              ${delivery.delivery.street_number} ${delivery.delivery.street}<br>
              ${delivery.delivery.postal_code} ${delivery.delivery.city}
            `;
            marker.bindPopup(popupContent);
          });

          // Ajouter le retour au Moulin
          routeCoordinates.push(MOULIN_COORDS);

          // Dessiner l'itinéraire
          const routeLine = L.polyline(routeCoordinates, {
            color: '#429853',
            weight: 4,
            opacity: 0.8
          }).addTo(map);

          // Ajuster la vue pour voir tout l'itinéraire
          map.fitBounds(routeLine.getBounds(), { padding: [20, 20] });

          // Nettoyer la carte quand le composant se démonte
          return () => {
            if (map) {
              map.remove();
            }
          };
        } catch (error) {
          console.error('Erreur lors du chargement de la carte:', error);
        }
      };

      loadLeaflet();
    }, [showMap, roadmap]);

    return (
      <div ref={mapRef} className={css(styles.mapContent)} />
    );
  };

  const sortDeliveries = (deliveries) => {
    return deliveries.sort((a, b) => {
      if (a.sort < b.sort) {
        return -1;
      }
      if (a.sort > b.sort) {
        return 1;
      }
      return 0;
    });
  };

  return (
    <div>
      {roadmap &&
      moment(roadmap.date).format("YYYY-MM-DD") ===
        moment(filters.date).format("YYYY-MM-DD") ? (
        <div>
          <div className={css(styles.center)}>
            <h2 className={css(styles.title)}>
              Tournée{" "}
              <b>{roadmap.name ? roadmap.name : "Pas de nom attribué"}</b> -{" "}
              {roadmap.delivery_man
                ? roadmap.delivery_man.firstname
                : "Aucun livreur (livreur supprimé ?)"}
            </h2>
            <div className={css(styles.center)}>
              <CreateRoadmapInput
                handleChange={updateDeliveryMan}
                defaultDeliveryMenId={
                  roadmap.delivery_man ? roadmap.delivery_man.id : null
                }
                hideAll={true}
              />
            </div>

            <Link
              to={{
                pathname: `/itineraires`,
                search: window.location.search,
              }}
              className={css(styles.back)}
            >
              Retour aux tournées
            </Link>

            <div className={css(styles.block)}>
              Départ à{" "}
              <b>
                {roadmap.start_hour
                  ? moment(roadmap.start_hour).format("HH:mm")
                  : "NC"}
              </b>{" "}
              | Trajet de <b>{roadmap?.distance ? (roadmap.distance / 1000).toFixed(1) : 0}</b> km
            </div>
            <div className={css(styles.buttonGroup)}>
              <button 
                onClick={saveRoadmap}
                disabled={localLoading}
                className={css({
                  padding: '10px 20px',
                  backgroundColor: localLoading ? '#ccc' : '#429853',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: localLoading ? 'not-allowed' : 'pointer',
                  '&:hover': {
                    backgroundColor: localLoading ? '#ccc' : '#3a8748'
                  }
                })}
              >
                {localLoading ? 'Sauvegarde en cours...' : 'Sauvegarder l\'itinéraire'}
              </button>
              <button 
                onClick={showMapPopup}
                className={css({
                  padding: '10px 20px',
                  backgroundColor: '#007bff',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: 'pointer',
                  '&:hover': {
                    backgroundColor: '#0056b3'
                  }
                })}
              >
                Voir sur la carte
              </button>
            </div>
          </div>
          <Droppable droppableId={roadmap.id}>
            {(provided, snapshot) => (
              <div
                ref={provided.innerRef}
                style={getListStyle(snapshot.isDraggingOver)}
              >
                {sortDeliveries(roadmap.deliveries).map(
                  (rmDelivery, rmDeliveryIndex) => (
                    <Draggable
                      isDragDisabled={dragDisabled}
                      key={
                        rmDelivery.recovery
                          ? `recovery-${rmDelivery.delivery.id}-${rmDeliveryIndex}`
                          : `delivery-${rmDelivery.delivery.id}-${rmDeliveryIndex}`
                      }
                      draggableId={
                        rmDelivery.recovery
                          ? `recovery-${rmDelivery.delivery.id}-${rmDeliveryIndex}`
                          : `delivery-${rmDelivery.delivery.id}-${rmDeliveryIndex}`
                      }
                      index={rmDeliveryIndex}
                    >
                      {(provided, snapshot) => (
                        <div
                          ref={provided.innerRef}
                          {...provided.draggableProps}
                          {...provided.dragHandleProps}
                          className={css({
                            opacity: dragDisabled ? "0.5" : "1",
                          })}
                        >
                          {rmDelivery.recovery ? (
                            <DeliveryRecoveryContainer
                              roadmapName={roadmap.name}
                              sort={rmDelivery.sort}
                              type="recovery"
                              delivery={rmDelivery.delivery}
                              colorDeliveryMan={
                                roadmap.delivery_man
                                  ? roadmap.delivery_man.color
                                  : "green"
                              }
                              roadmapId={roadmap.id}
                              theme="light"
                              recoveryReallocation={() => {
                                return recoveryReallocation(rmDeliveryIndex);
                              }}
                            />
                          ) : (
                            <DeliveryRecoveryContainer
                              type="delivery"
                              delivery={rmDelivery.delivery}
                              sort={rmDelivery.sort}
                              roadmapName={roadmap.name}
                              colorDeliveryMan={
                                roadmap.delivery_man
                                  ? roadmap.delivery_man.color
                                  : "green"
                              }
                              roadmapId={roadmap.id}
                              theme="light"
                              recoveryReallocation={() => {
                                return recoveryReallocation(rmDeliveryIndex);
                              }}
                            />
                          )}
                        </div>
                      )}
                    </Draggable>
                  )
                )}
                {provided.placeholder}
              </div>
            )}
          </Droppable>
        </div>
      ) : (
        <div className={css(styles.center)}>
          <h2 className={css(styles.title)}>
            Cette tournée n'existe pas pour ces dates/livreurs.
          </h2>
          <Link
            to={{
              pathname: `/itineraires`,
              search: window.location.search,
            }}
            className={css(styles.back)}
          >
            Retour aux tournées
          </Link>
        </div>
      )}

      {/* Popup de carte */}
      {showMap && (
        <div className={css(styles.mapPopup)} onClick={closeMapPopup}>
          <div className={css(styles.mapContainer)} onClick={(e) => e.stopPropagation()}>
            <button 
              className={css(styles.mapCloseButton)}
              onClick={closeMapPopup}
            >
              ×
            </button>
            <RouteMap />
          </div>
        </div>
      )}
    </div>
  );
};

OptimizedRouteDetail.propTypes = {
  roadmap: PropTypes.object,
  dragDisabled: PropTypes.bool,
  recoveryReallocation: PropTypes.func,
  handleChangeDeliveryMan: PropTypes.func,
  onRoadmapSaved: PropTypes.func,
};

export default OptimizedRouteDetail;